import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import products from './products.json';
import './index.css';
import FormulatorRow from './FormulatorRow';
import FormulatorHeader from './FormulatorHeader.png';
import { auth, db } from './firebase';
import { doc, setDoc, addDoc, collection, getDoc, runTransaction } from 'firebase/firestore';
import debounce from 'lodash/debounce';

// Derive dropdown options and costs from products.json
const vehicleOptions = products.vehicles.map(p => p.name);
const binderOptions = products.binders.map(p => p.name);
const colorantOptions = products.colorants.map(p => p.name);
const ingredientCosts = Object.fromEntries(
  [...products.vehicles, ...products.binders, ...products.colorants]
    .filter(p => p.name !== '------------------------------' && p.costPerGram !== undefined)
    .map(p => [p.name, p.costPerGram])
);

const stainTypeOptions = products.stainTypes || [];

const sections = [
  { title: "Stain Base", sectionClass: "vehicle", rows: [
    { label: "Stain Base", options: vehicleOptions },
  ]},
  { title: "Additives", sectionClass: "binder", rows: [
    { label: "Additives 1", options: binderOptions },
    { label: "Additives 2", options: binderOptions }
  ]},
  { title: "Colorants", sectionClass: "colorant", rows: [
    { label: "Colorant 1", options: colorantOptions },
    { label: "Colorant 2", options: colorantOptions },
    { label: "Colorant 3", options: colorantOptions },
    { label: "Colorant 4", options: colorantOptions },
    { label: "Colorant 5", options: colorantOptions },
    { label: "Colorant 6", options: colorantOptions },
    { label: "Colorant 7", options: colorantOptions }
  ]}
];

// Webcam helper functions
const startWebcam = async (videoRef) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
    return stream;
  } catch (err) {
    console.error("Error accessing webcam:", err);
    let message = "Could not access webcam. Please ensure it's connected and permissions are granted.";
    if (err.name === "NotAllowedError") {
      message = "Webcam access was denied. Please allow camera permissions in your browser settings.";
    } else if (err.name === "NotFoundError") {
      message = "No webcam found. Please connect a camera and try again.";
    }
    alert(message);
    return null;
  }
};

const stopWebcam = (stream) => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop());
  }
};

function Formulator() {
  console.log("Rendering Formulator");
  const navigate = useNavigate();
  const location = useLocation();
  const { formulaToEdit, formulaToStart, initialEditMode, fromReTint } = location.state || {};

  const [formulationVehicleStart, setFormulationVehicleStart] = useState(
    formulaToEdit ? formulaToEdit.formulationVehicleStart : (formulaToStart?.formulationVehicleStart || '')
  );
  const [binderPercentage, setBinderPercentage] = useState(
    formulaToEdit ? formulaToEdit.binderPercentage : (formulaToStart?.binderPercentage || '')
  );
  const [customVehicle1, setCustomVehicle1] = useState(
    formulaToEdit ? formulaToEdit.customVehicle1 : (formulaToStart?.customVehicle1 || '')
  );
  const [standardSize, setStandardSize] = useState(
    formulaToEdit ? formulaToEdit.standardSize : (formulaToStart?.standardSize || '')
  );
  const [rowsData, setRowsData] = useState(() => {
    const defaultRows = sections.flatMap(section =>
      section.rows.map(row => ({
        label: row.label,
        options: row.options,
        selectedItem: '',
        hits: ['', '', '', ''],
        sectionClass: section.sectionClass
      }))
    );
    console.log("Initialized rowsData:", defaultRows);
    if (formulaToEdit?.rowsData || formulaToStart?.rowsData) {
      const sourceRows = (formulaToEdit || formulaToStart).rowsData || [];
      const updatedRows = defaultRows.map((defaultRow, index) => ({
        ...defaultRow,
        selectedItem: sourceRows[index]?.selectedItem || '',
        hits: sourceRows[index]?.hits || ['', '', '', '']
      }));
      console.log("Updated rowsData with source:", updatedRows);
      return updatedRows;
    }
    return defaultRows;
  });
  const [colorName, setColorName] = useState(formulaToEdit ? formulaToEdit.colorName : '');
  const [woodSpecies, setWoodSpecies] = useState(formulaToEdit ? formulaToEdit.woodSpecies : '');
  const [customer, setCustomer] = useState(formulaToEdit ? formulaToEdit.customer : '');
  const [date, setDate] = useState(
    formulaToEdit ? formulaToEdit.date : new Date().toISOString().split('T')[0]
  );
  const [formulator, setFormulator] = useState(formulaToEdit ? formulaToEdit.formulator : '');
  const [tintId, setTintId] = useState(formulaToEdit ? formulaToEdit.tintId : null);
  const [isEditMode, setIsEditMode] = useState(initialEditMode !== undefined ? initialEditMode : true);
  const [isTintPopupOpen, setIsTintPopupOpen] = useState(false);
  const [tintMode, setTintMode] = useState(null);
  const [tintIngredients, setTintIngredients] = useState([]);
  const [uploadedImage, setUploadedImage] = useState(formulaToEdit ? formulaToEdit.uploadedImage : null);
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const [isWebcamPopupOpen, setIsWebcamPopupOpen] = useState(false);
  const [notes, setNotes] = useState(formulaToEdit ? formulaToEdit.notes : '');
  const [isSavePopupOpen, setIsSavePopupOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [isInfoEditMode, setIsInfoEditMode] = useState(false);
  const [infoEditData, setInfoEditData] = useState({
    colorName: colorName || '',
    woodSpecies: woodSpecies || '',
    customer: customer || '',
    date: date || new Date().toISOString().split('T')[0],
    formulator: formulator || '',
    stainType: stainTypeOptions.length > 0 ? stainTypeOptions[0] : ''
  });

  const [saveAsData, setSaveAsData] = useState({
    colorName: '',
    woodSpecies: '',
    customer: '',
    date: new Date().toISOString().split('T')[0],
    formulator: '',
    stainType: ''
  });

  const [stainType, setStainType] = useState(formulaToEdit ? formulaToEdit.stainType : '');
  const [tintPhase, setTintPhase] = useState('input');
  const [corrections, setCorrections] = useState([]);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const saveResolver = useRef(null);
  const tintTableRefs = useRef([]);

  const limitInput = value => value.length > 7 ? value.slice(0, 7) : value;

  const handleVehicleStartChange = e => { 
    console.log("Vehicle Start changed to:", e.target.value);
    setFormulationVehicleStart(limitInput(e.target.value)); 
    setIsDirty(true); 
  };
  const handleBinderPercentageChange = e => { 
    console.log("Binder Percentage changed to:", e.target.value);
    setBinderPercentage(limitInput(e.target.value)); 
    setIsDirty(true); 
  };
  const handleNotesChange = e => { setNotes(e.target.value); setIsDirty(true); };
  const handleImageChange = image => { setUploadedImage(image); setIsDirty(true); };
  const handleColorNameChange = e => { setColorName(e.target.value); setIsDirty(true); };
  const handleWoodSpeciesChange = e => { setWoodSpecies(e.target.value); setIsDirty(true); };
  const handleCustomerChange = e => { setCustomer(e.target.value); setIsDirty(true); };
  const handleDateChange = e => { setDate(e.target.value); setIsDirty(true); };
  const handleFormulatorChange = e => { setFormulator(e.target.value); setIsDirty(true); };
  const handleStainTypeChange = e => { setStainType(e.target.value); setIsDirty(true); };

  const applyToMainTable = () => {
    if (formulationVehicleStart && binderPercentage) {
      const vehicleAmount = parseFloat(formulationVehicleStart);
      const binderPercent = parseFloat(binderPercentage) / 100;
      const binderValue = (vehicleAmount * binderPercent) / (1 - binderPercent);

      const newRowsData = [...rowsData];
      newRowsData[0].hits[0] = vehicleAmount.toFixed(1);
      newRowsData[1].hits[0] = binderValue.toFixed(1);
      setRowsData(newRowsData);
      setIsDirty(true);
    }
  };

  const saveFormula = async () => {
    if (!auth.currentUser) {
      alert("You must be logged in to save a formula.");
      return { saved: false };
    }

    if (!tintId) {
      setIsSavePopupOpen(true);
      return new Promise((resolve) => {
        saveResolver.current = resolve;
      });
    }

    await saveFormulaToFirestore();
    return { saved: true };
  };

  const saveFormulaToFirestore = async (popupData = null) => {
    try {
      console.log("Saving formula with data:", {
        tintId,
        colorName: popupData ? popupData.colorName : colorName,
        woodSpecies: popupData ? popupData.woodSpecies : woodSpecies,
        customer: popupData ? popupData.customer : customer,
        date: popupData ? popupData.date : date,
        formulator: popupData ? popupData.formulator : formulator,
        rowsData,
        formulationVehicleStart,
        binderPercentage,
        customVehicle1,
        standardSize,
        uploadedImage,
        userId: auth.currentUser.uid,
        notes
      });

      if (!auth.currentUser) {
        throw new Error("User not authenticated");
      }

      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        throw new Error("User document not found. Please sign up again.");
      }
      const companyInitials = userDoc.data().companyInitials || 'XX';

      const now = new Date();
      const datePrefix = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getFullYear()).slice(-2)}`;

      let newTintId = tintId;
      if (!tintId) {
        await runTransaction(db, async (transaction) => {
          const userMetaRef = doc(db, 'users', auth.currentUser.uid);
          const userMetaDoc = await transaction.get(userMetaRef);
          const userMeta = userMetaDoc.exists() ? userMetaDoc.data() : {};
          const dateKey = datePrefix;
          const currentCount = userMeta[dateKey] || 0;
          const newCount = currentCount + 1;
          newTintId = `${companyInitials}-${datePrefix}${newCount}`;

          transaction.set(userMetaRef, { [dateKey]: newCount }, { merge: true });
        });
      }

      if (!tintId && (!popupData || !popupData.colorName)) {
        throw new Error("Color Name is required for new formulas.");
      }

      const formula = {
        tintId: newTintId,
        colorName: popupData ? popupData.colorName || '' : colorName || '',
        woodSpecies: popupData ? popupData.woodSpecies || '' : woodSpecies || '',
        customer: popupData ? popupData.customer || '' : customer || '',
        date: popupData ? popupData.date || new Date().toISOString().split('T')[0] : date || new Date().toISOString().split('T')[0],
        formulator: popupData ? popupData.formulator || '' : formulator || '',
        rowsData: rowsData.map(row => ({
          label: row.label,
          options: row.options,
          selectedItem: row.selectedItem || '',
          hits: row.hits || ['', '', '', ''],
          sectionClass: row.sectionClass
        })),
        formulationVehicleStart: formulationVehicleStart || '',
        binderPercentage: binderPercentage || '',
        customVehicle1: customVehicle1 || '',
        standardSize: standardSize || '',
        uploadedImage: uploadedImage || null,
        userId: auth.currentUser.uid,
        createdAt: now.toISOString(),
        notes: notes || '',
        stainType: popupData ? popupData.stainType || '' : stainType || ''
      };

      if (fromReTint && formulaToEdit && formulaToEdit.id) {
        console.log('Attempting to update formula:', { formulaId: formulaToEdit.id, formulaUserId: formulaToEdit.userId, currentUserUid: auth.currentUser?.uid });
        if (formulaToEdit.userId !== auth.currentUser?.uid) {
          console.error('UserId mismatch - cannot update');
          alert('Cannot update: This formula belongs to a different user.');
          return;
        }
        await setDoc(doc(db, 'formulas', formulaToEdit.id), formula);
        console.log("Formula updated in Firestore:", formula);
      } else {
        const docRef = await addDoc(collection(db, 'formulas'), formula);
        console.log("Formula saved to Firestore:", { ...formula, id: docRef.id });
      }

      setTintId(newTintId);
      setColorName(formula.colorName);
      setWoodSpecies(formula.woodSpecies);
      setCustomer(formula.customer);
      setDate(formula.date);
      setFormulator(formula.formulator);
      setIsDirty(false);
      setIsSavePopupOpen(true);
      setTimeout(() => {
        setIsSavePopupOpen(false);
      }, 2000);
    } catch (error) {
      console.error("Error saving formula:", error);
      alert(`Failed to save formula: ${error.message}`);
    }
  };

  const handleSaveAsSubmit = () => {
    if (!saveAsData.colorName) {
      alert("Please enter a Color Name.");
      return;
    }
    console.log('Saving with popup data:', saveAsData);
    setIsSavePopupOpen(false);
    saveFormulaToFirestore(saveAsData);
    if (saveResolver.current) {
      saveResolver.current({ saved: true });
      saveResolver.current = null;
    }
  };

  const clearFormula = () => {
    setFormulationVehicleStart('');
    setBinderPercentage('');
    setCustomVehicle1('');
    setStandardSize('');
    setColorName('');
    setWoodSpecies('');
    setCustomer('');
    setDate(new Date().toISOString().split('T')[0]);
    setFormulator('');
    setUploadedImage(null);
    setNotes('');
    setRowsData(
      sections.flatMap(section =>
        section.rows.map(row => ({
          label: row.label,
          options: row.options,
          selectedItem: '',
          hits: ['', '', '', ''],
          sectionClass: row.sectionClass
        }))
      )
    );
    setTintId(null);
    setIsDirty(false);
  };

  const handleNavigateToLibrary = () => {
    console.log("handleNavigateToLibrary called", { 
      isDirty, 
      isEditMode, 
      tintId,
      colorName,
      woodSpecies,
      customer,
      date,
      formulator
    });
    if (isDirty && isEditMode) {
      console.log("Showing confirmation popup");
      setIsConfirmPopupOpen(true);
    } else {
      console.log("Navigating directly to library");
      try {
        navigate('/');
        console.log("Navigation completed");
      } catch (error) {
        console.error("Navigation error:", error);
      }
    }
  };

  const handleConfirmSave = async () => {
    console.log("Saving formula before navigation");
    setIsConfirmPopupOpen(false);
    const result = await saveFormula();
    if (result.saved) {
      console.log("Saved and navigating to library");
      try {
        navigate('/');
        console.log("Navigation after save completed");
      } catch (error) {
        console.error("Navigation after save error:", error);
      }
    } else {
      console.log("Save cancelled, staying in Formulator");
    }
  };

  const handleConfirmNo = () => {
    console.log("Discarding changes");
    setIsConfirmPopupOpen(false);
    setIsDirty(false);
    console.log("Discarding changes and navigating to library");
    try {
      navigate('/');
      console.log("Navigation after discard completed");
    } catch (error) {
      console.error("Navigation after discard error:", error);
    }
  };

  const handleConfirmCancel = () => {
    setIsConfirmPopupOpen(false);
  };

  const openWebcamPopup = () => {
    setIsWebcamPopupOpen(true);
  };

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageDataUrl = canvas.toDataURL('image/png');
      handleImageChange(imageDataUrl);
      setIsWebcamPopupOpen(false);
    }
  };

  useEffect(() => {
    let stream = null;
    if (isWebcamPopupOpen) {
      startWebcam(videoRef).then(s => (stream = s));
    }
    return () => {
      stopWebcam(stream);
    };
  }, [isWebcamPopupOpen]);

  const openImagePopup = (e) => {
    e.preventDefault();
    setIsImagePopupOpen(true);
  };

  const computeRowTotal = hits => hits.reduce((sum, hit) => sum + (parseFloat(hit) || 0), 0).toFixed(1);
  const grandTotal = rowsData.reduce((sum, row) => sum + parseFloat(computeRowTotal(row.hits)), 0).toFixed(1);

  const calculateTotals = () => {
    console.log("Calculating totals with:", { rowsData, binderPercentage, customVehicle1, standardSize });
    const vehicleRows = rowsData.filter(row => row.sectionClass === "vehicle");
    const binderRows = rowsData.filter(row => row.sectionClass === "binder");
    const colorantRows = rowsData.filter(row => row.sectionClass === "colorant");

    const vehicleAmount = vehicleRows.reduce((sum, row) => sum + parseFloat(computeRowTotal(row.hits) || 0), 0).toFixed(1);
    const vehicle1Amount = parseFloat(computeRowTotal(rowsData[0]?.hits) || 0).toFixed(1);
    const currentBinderAmount = binderRows.reduce((sum, row) => sum + parseFloat(computeRowTotal(row.hits) || 0), 0).toFixed(1);
    const colorantAmount = colorantRows.reduce((sum, row) => sum + parseFloat(computeRowTotal(row.hits) || 0), 0).toFixed(1);

    const totalBase = (parseFloat(vehicleAmount) + parseFloat(currentBinderAmount)).toFixed(1);
    const binderRatio = vehicle1Amount > 0 ? parseFloat(currentBinderAmount) / parseFloat(vehicle1Amount) : 0;

    const nonBinderTotal = (parseFloat(vehicleAmount) + parseFloat(colorantAmount)).toFixed(1);
    const binderPercent = parseFloat(binderPercentage) / 100 || 0;
    const totalFormulaNeeded = binderPercent > 0 ? (nonBinderTotal / (1 - binderPercent)).toFixed(1) : 0;
    const currentTotal = (parseFloat(vehicleAmount) + parseFloat(currentBinderAmount) + parseFloat(colorantAmount)).toFixed(1);
    const targetBinderAmount = (totalFormulaNeeded * binderPercent).toFixed(1);
    const additionalBinderNeeded = binderPercent > 0 ? (parseFloat(targetBinderAmount) - parseFloat(currentBinderAmount)).toFixed(1) : 0;

    // Custom Scaling
    const customVehicle1Value = parseFloat(customVehicle1) || 0;
    let customBinderValue = customVehicle1Value * binderRatio;
    // Optional: Add projected additional binder if desired (uncomment to enable)
    // customBinderValue += parseFloat(additionalBinderNeeded) * (customVehicle1Value / parseFloat(vehicle1Amount || 1));
    const customTotalBase = (customVehicle1Value + customBinderValue).toFixed(1);
    const customValues = rowsData.map(row => {
      const rowTotal = parseFloat(computeRowTotal(row.hits) || 0);
      if (row.sectionClass === 'colorant') {
        const percentageOfBase = totalBase > 0 ? rowTotal / parseFloat(totalBase) : 0;
        return customTotalBase > 0 ? (parseFloat(customTotalBase) * percentageOfBase).toFixed(1) : '';
      } else {
        const percentageOfVehicle1 = vehicle1Amount > 0 ? rowTotal / parseFloat(vehicle1Amount) : 0;
        return customVehicle1Value > 0 ? (customVehicle1Value * percentageOfVehicle1).toFixed(1) : '';
      }
    });

    // Standard Scaling
    const standardSizesMap = {
      "Quart": 850,
      "Gallon": 3550,
      "5 Gallon": 20000
    };
    const desiredTotal = standardSizesMap[standardSize] || 0;
    const scaleFactor = grandTotal > 0 ? desiredTotal / parseFloat(grandTotal) : 0;
    const standardValues = rowsData.map(row => {
      const rowTotal = parseFloat(computeRowTotal(row.hits) || 0);
      return desiredTotal > 0 ? (rowTotal * scaleFactor).toFixed(1) : '';
    });

    const customCost = rowsData.reduce((sum, row, index) => {
      const amount = parseFloat(customValues[index]) || 0;
      const costPerGram = ingredientCosts[row.selectedItem] || 0;
      return sum + (amount * costPerGram);
    }, 0).toFixed(2);

    const standardCost = rowsData.reduce((sum, row, index) => {
      const amount = parseFloat(standardValues[index]) || 0;
      const costPerGram = ingredientCosts[row.selectedItem] || 0;
      return sum + (amount * costPerGram);
    }, 0).toFixed(2);

    return {
      vehicleAmount,
      currentBinderAmount,
      colorantAmount,
      additionalBinderNeeded,
      currentBinderPercentage: currentTotal > 0 ? (parseFloat(currentBinderAmount) / parseFloat(currentTotal) * 100).toFixed(1) : 0,
      colorantPercentage: grandTotal > 0 ? (parseFloat(colorantAmount) / parseFloat(grandTotal) * 100).toFixed(1) : 0,
      customValues,
      standardValues,
      customCost,
      standardCost
    };
  };

  const totals = useMemo(() => calculateTotals(), [rowsData, binderPercentage, customVehicle1, standardSize]);

  const openTintPopup = (mode) => {
    const values = mode === 'custom' ? totals.customValues : totals.standardValues;
    const ingredients = rowsData
      .map((row, index) => ({
        label: row.selectedItem || row.label,
        totalWeight: values[index],
        actualPour: '',
        correctedWeight: values[index],
        adjustment: ''
      }))
      .filter(ing => ing.totalWeight !== '' && parseFloat(ing.totalWeight) > 0);
    if (ingredients.length > 0) {
      setTintMode(mode);
      setTintIngredients(ingredients);
      setTintPhase('input');
      setCorrections([]);
      setIsTintPopupOpen(true);
    } else {
      alert('No ingredients with values to tint.');
    }
  };

  const debouncedHandleHitChange = debounce((globalRowIndex, hitIndex, value) => {
    console.log(`Formulator: onHitChange row=${globalRowIndex}, hit=${hitIndex}, value=${value}`);
    if (value === undefined || value === null) return;
    const newRowsData = [...rowsData];
    newRowsData[globalRowIndex].hits[hitIndex] = value;
    setRowsData(newRowsData);
    setIsDirty(true);
  }, 100);

  const debouncedHandleHitBlur = debounce((globalRowIndex, hitIndex, value) => {
    console.log(`Formulator: onBlur row=${globalRowIndex}, hit=${hitIndex}, value=${value}`);
    const newRowsData = [...rowsData];
    const parsedValue = value === '' ? '' : parseFloat(value).toFixed(1);
    newRowsData[globalRowIndex].hits[hitIndex] = parsedValue;
    setRowsData(newRowsData);
    setIsDirty(true);
  }, 100);

  const exportToPDF = () => {
    if (!rowsData.every(row => row.hits && row.hits.length === 4)) {
      alert("Invalid formula data. Please check your inputs.");
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
  
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Formula: ${tintId || 'New'} - ${colorName || 'Unnamed'}`, 10, 10);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Wood Species: ${woodSpecies || 'N/A'} | Customer: ${customer || 'N/A'}`, 10, 20);
    doc.text(`Date: ${date} | Stain Type: ${stainType || 'N/A'}`, 10, 30);
  
    const headers = ['Ingredient', 'Shot 1', 'Shot 2', 'Shot 3', 'Shot 4', 'Total', 'Percentage', 'Custom Size', 'Standard Sizes'];
    const columnWidths = [70, 25, 25, 25, 25, 25, 25, 27, 30];
    let y = 38;
  
    doc.setFontSize(10.5);
    doc.setFont("helvetica", "bold");
    headers.forEach((header, index) => {
      const x = 10 + columnWidths.slice(0, index).reduce((a, b) => a + b, 0);
      doc.setFillColor(224, 224, 224);
      doc.rect(x, y, columnWidths[index], 10, 'F');
      doc.setTextColor(0, 0, 0);
      doc.text(header, x + 2, y + 7);
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    y += 10;
  
    sections.forEach(section => {
      const sectionRow = [section.title, '', '', '', '', '', '', '', ''];
      sectionRow.forEach((cell, index) => {
        const x = 10 + columnWidths.slice(0, index).reduce((a, b) => a + b, 0);
        if (index === 0) {
          doc.setFillColor(224, 224, 224);
        } else {
          doc.setFillColor(255, 255, 255);
        }
        doc.rect(x, y, columnWidths[index], 10, 'F');
        doc.setTextColor(0, 0, 0);
        doc.text(cell, x + 2, y + 7, { maxWidth: columnWidths[index] - 4 });
      });
      y += 10;
  
      section.rows.forEach((row, rowIndex) => {
        const globalRowIndex = sections.slice(0, sections.indexOf(section)).reduce((sum, s) => sum + s.rows.length, 0) + rowIndex;
        const data = rowsData[globalRowIndex];
        const rowData = [
          data.selectedItem || row.label,
          data.hits[0] ? `${data.hits[0]}g` : '',
          data.hits[1] ? `${data.hits[1]}g` : '',
          data.hits[2] ? `${data.hits[2]}g` : '',
          data.hits[3] ? `${data.hits[3]}g` : '',
          computeRowTotal(data.hits) ? `${computeRowTotal(data.hits)}g` : '',
          grandTotal > 0 ? `${((parseFloat(computeRowTotal(data.hits)) / parseFloat(grandTotal)) * 100).toFixed(1)}%` : '',
          totals.customValues[globalRowIndex] ? `${totals.customValues[globalRowIndex]}g` : '',
          totals.standardValues[globalRowIndex] ? `${totals.standardValues[globalRowIndex]}g` : ''
        ];
  
        rowData.forEach((cell, index) => {
          const x = 10 + columnWidths.slice(0, index).reduce((a, b) => a + b, 0);
          doc.setFillColor(255, 255, 255);
          doc.rect(x, y, columnWidths[index], 10, 'F');
          doc.setTextColor(0, 0, 0);
          doc.text(cell, x + 2, y + 7, { maxWidth: columnWidths[index] - 4 });
        });
        y += 10;
      });
    });
  
    doc.setFillColor(200, 200, 200);
    doc.rect(10, y, 277, 10, 'F');
    doc.setTextColor(0, 0, 0);
    doc.text('Total', 10, y + 7);
    doc.text(grandTotal > 0 ? `${grandTotal}g` : '', 10 + columnWidths.slice(0, 5).reduce((a, b) => a + b, 0) + 2, y + 7);
    doc.save(`Formula_${tintId || 'New'}_${colorName || 'Unnamed'}.pdf`);
  };

  const handleTintKeyDown = (e, index) => {
    if (e.key === 'ArrowDown' && index < tintIngredients.length - 1) {
      tintTableRefs.current[index + 1].focus();
    } else if (e.key === 'ArrowUp' && index > 0) {
      tintTableRefs.current[index - 1].focus();
    }
  };

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackName, setFeedbackName] = useState('');

  const handleSendFeedback = async () => {
    if (!feedbackText.trim()) return alert('Please enter feedback.');
    if (!feedbackName.trim()) return alert('Please enter your name.');
    try {
      await addDoc(collection(db, 'feedback'), {
        userId: auth.currentUser?.uid || 'anonymous',
        name: feedbackName.trim(),
        timestamp: new Date().toISOString(),
        notes: feedbackText
      });
      alert('Feedback sent! Thank you.');
      setIsFeedbackOpen(false);
      setFeedbackText('');
      setFeedbackName('');
    } catch (error) {
      alert('Failed to send feedback: ' + error.message);
    }
  };

  return (
    <div>
      <img src={FormulatorHeader} alt="Formulator Header" className="header-logo" />
      <div style={{ display: 'flex', gap: '10px' }}>
        <div className="formula-details-box" style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '250px', border: '0.5px solid #ccc', padding: '10px' }}>
          <h2>Formula Details</h2>
          <div className="edit-toggle-container">
            <label>Edit: {isEditMode ? 'ON' : 'OFF'}</label>
            <label className="switch">
              <input type="checkbox" checked={isEditMode} onChange={() => setIsEditMode(!isEditMode)} />
              <span className="slider round"></span>
            </label>
          </div>
          <div className="formula-inputs-container">
            <input
              type="number"
              value={binderPercentage}
              onChange={handleBinderPercentageChange}
              placeholder="Binder Load %"
              className="formula-input"
              maxLength="7"
              readOnly={!isEditMode}
            />
            <input
              type="number"
              value={formulationVehicleStart}
              onChange={handleVehicleStartChange}
              placeholder="Vehicle Start"
              className="formula-input"
              maxLength="7"
              readOnly={!isEditMode}
            />
            <button 
              onClick={applyToMainTable} 
              disabled={!isEditMode}
              className="enter-values-button"
            >
              Enter Values
            </button>
            <hr style={{ border: '1px solid #ccc', margin: '10px 0' }} />
          </div>
          {tintId && (
            <div className="formula-info" style={{ color: '#fff' }}>
              {isInfoEditMode ? (
                <>
                  <p><strong>Tint ID:</strong> {tintId}</p>
                  <p><strong>Color Name:</strong> <input type="text" value={infoEditData.colorName} onChange={e => setInfoEditData({ ...infoEditData, colorName: e.target.value })} /></p>
                  <p><strong>Wood Species:</strong> <input type="text" value={infoEditData.woodSpecies} onChange={e => setInfoEditData({ ...infoEditData, woodSpecies: e.target.value })} /></p>
                  <p><strong>Customer:</strong> <input type="text" value={infoEditData.customer} onChange={e => setInfoEditData({ ...infoEditData, customer: e.target.value })} /></p>
                  <p><strong>Date:</strong> <input type="date" value={infoEditData.date} onChange={e => setInfoEditData({ ...infoEditData, date: e.target.value })} /></p>
                  <p><strong>Formulator:</strong> <input type="text" value={infoEditData.formulator} onChange={e => setInfoEditData({ ...infoEditData, formulator: e.target.value })} /></p>
                  <p><strong>Stain Type:</strong> <select value={infoEditData.stainType} onChange={e => setInfoEditData({ ...infoEditData, stainType: e.target.value })} style={{ width: '100%', padding: '8px', backgroundColor: '#373636', color: '#fff', border: '1px solid #ccc', borderRadius: '4px', fontSize: '16px' }}>
                    <option value="">Select Stain Type</option>
                    {stainTypeOptions.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select></p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button onClick={async () => {
                      // Save info changes
                      setColorName(infoEditData.colorName);
                      setWoodSpecies(infoEditData.woodSpecies);
                      setCustomer(infoEditData.customer);
                      setDate(infoEditData.date);
                      setFormulator(infoEditData.formulator);
                      setStainType(infoEditData.stainType);
                      setIsInfoEditMode(false);
                      setIsDirty(true);
                      // If formula is saved, update Firestore
                      if (tintId && auth.currentUser && formulaToEdit && formulaToEdit.id) {
                        try {
                          await setDoc(doc(db, 'formulas', formulaToEdit.id), {
                            ...formulaToEdit,
                            colorName: infoEditData.colorName,
                            woodSpecies: infoEditData.woodSpecies,
                            customer: infoEditData.customer,
                            date: infoEditData.date,
                            formulator: infoEditData.formulator,
                            stainType: infoEditData.stainType
                          }, { merge: true });
                        } catch (error) {
                          alert('Failed to update formula info: ' + error.message);
                        }
                      }
                    }}>Save</button>
                    <button onClick={() => {
                      setInfoEditData({
                        colorName,
                        woodSpecies,
                        customer,
                        date,
                        formulator,
                        stainType
                      });
                      setIsInfoEditMode(false);
                    }}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Tint ID:</strong> {tintId}</p>
                  <p><strong>Color Name:</strong> {colorName || 'N/A'}</p>
                  <p><strong>Wood Species:</strong> {woodSpecies || 'N/A'}</p>
                  <p><strong>Customer:</strong> {customer || 'N/A'}</p>
                  <p><strong>Date:</strong> {date}</p>
                  <p><strong>Formulator:</strong> {formulator || 'N/A'}</p>
                  <p><strong>Stain Type:</strong> {stainType || 'N/A'}</p>
                  <button style={{ marginTop: '10px' }} onClick={() => setIsInfoEditMode(true)}>Edit Info</button>
                </>
              )}
            </div>
          )}
          <hr style={{ border: '1px solid #ccc', margin: '10px 0' }} />
          <table className="formula-details-summary-table">
            <tbody>
              <tr>
                <td>Colorant Load:</td>
                <td>
                  <input
                    type="text"
                    value={totals.colorantPercentage > 0 ? `${totals.colorantPercentage}%` : ''}
                    readOnly
                    className="colorant-load"
                  />
                </td>
              </tr>
              <tr>
                <td>Binder Load:</td>
                <td>
                  <input
                    type="text"
                    value={totals.currentBinderPercentage > 0 ? `${totals.currentBinderPercentage}% + ${totals.additionalBinderNeeded}g` : ''}
                    readOnly
                    className={`binder-load ${parseFloat(totals.additionalBinderNeeded) === 0 ? 'binder-load-green' : 'binder-load-red'}`}
                  />
                </td>
              </tr>
              <tr>
                <td>Custom Cost:</td>
                <td>
                  <input
                    type="text"
                    value={totals.customCost > 0 ? `$${totals.customCost}` : ''}
                    readOnly
                    className="custom-cost"
                  />
                </td>
              </tr>
              <tr>
                <td>Standard Cost:</td>
                <td>
                  <input
                    type="text"
                    value={totals.standardCost > 0 ? `$${totals.standardCost}` : ''}
                    readOnly
                    className="standard-cost"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          {uploadedImage && (
            <button 
              onClick={openImagePopup} 
              className="view-image-button"
              disabled={!isEditMode}
            >
              View Captured Image
            </button>
          )}
          <textarea
            value={notes}
            onChange={handleNotesChange}
            placeholder="Notes"
            className="notes-textarea"
            readOnly={!isEditMode}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div className="formulator-action-buttons">
            <button onClick={handleNavigateToLibrary} className="go-to-formulas-btn">Go to Formula Library</button>
            <button onClick={openWebcamPopup} disabled={!isEditMode} className="capture-image-btn">Capture Image</button>
            <button onClick={saveFormula} disabled={!isEditMode && !fromReTint} className="save-formula-btn">Save Formula</button>
            <button onClick={clearFormula} disabled={!isEditMode} className="clear-formula-btn">Clear Formula</button>
            <button onClick={exportToPDF} className="print-button">Download PDF</button>
            <button onClick={() => setIsFeedbackOpen(true)} className="feedback-btn">Developer Feedback</button>
          </div>
          <table className="table-dark formulation-table">
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>Shot 1</th>
                <th>Shot 2</th>
                <th>Shot 3</th>
                <th>Shot 4</th>
                <th>Total</th>
                <th>%</th>
                <th>
                  <button onClick={() => openTintPopup('custom')} style={{ 
                    backgroundColor: '#495057',
                    color: '#ffc107',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer'
                  }}>Tint Custom</button>
                </th>
                <th>
                  <button onClick={() => openTintPopup('standard')} style={{ 
                    backgroundColor: '#495057',
                    color: '#28a745',
                    border: '1px solid #28a745',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer'
                  }}>Tint Standard</button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  <tr className={`section-header ${section.sectionClass}-header`}>
                    <td>{section.title}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  {section.rows.map((row, rowIndex) => {
                    const globalRowIndex = sections.slice(0, sections.indexOf(section)).reduce((sum, s) => sum + s.rows.length, 0) + rowIndex;
                    return (
                      <FormulatorRow
                        key={globalRowIndex}
                        data={rowsData[globalRowIndex]}
                        onSelectedItemChange={(value) => {
                          console.log(`Ingredient dropdown changed for row ${globalRowIndex} to:`, value);
                          const newRowsData = [...rowsData];
                          newRowsData[globalRowIndex].selectedItem = value;
                          setRowsData(newRowsData);
                          setIsDirty(true);
                        }}
                        onHitChange={(hitIndex, value) => debouncedHandleHitChange(globalRowIndex, hitIndex, value)}
                        onBlur={(hitIndex, value) => debouncedHandleHitBlur(globalRowIndex, hitIndex, value)}
                        grandTotal={grandTotal}
                        customValue={totals.customValues[globalRowIndex]}
                        isVehicle1={globalRowIndex === 0}
                        onCustomChange={(value) => {
                          console.log(`Custom Vehicle1 changed to:`, value);
                          setCustomVehicle1(value);
                          setIsDirty(true);
                        }}
                        customVehicle1={customVehicle1}
                        standardValue={totals.standardValues[globalRowIndex]}
                        onStandardChange={(value) => {
                          console.log(`Standard Size changed to:`, value);
                          setStandardSize(value);
                          setIsDirty(true);
                        }}
                        standardSize={standardSize}
                        isEditableStandard={globalRowIndex === 0}
                        isEditMode={isEditMode}
                        fromReTint={fromReTint}
                        showTintButton={globalRowIndex === 0}
                        onTintStandard={() => openTintPopup('standard')}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="grand-total">
                <td colSpan="5"></td>
                <td>{grandTotal > 0 ? `${grandTotal}g` : ''}</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          {isTintPopupOpen && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ width: '95%', height: '95%', backgroundColor: '#373636', padding: '20px', borderRadius: '12px', overflow: 'auto', color: '#fff' }}>
                <h2 style={{ fontSize: '3em', fontWeight: 'bold', textAlign: 'center', marginBottom: '20px' }}>Dispense - {tintMode === 'custom' ? 'Custom' : 'Standard'}</h2>
                {tintPhase === 'input' ? (
                  <>
                    <table style={{ width: '100%', fontSize: '3em', fontWeight: 'bold', borderCollapse: 'collapse' }}>
                      <colgroup>
                        <col style={{ width: '60%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                      </colgroup>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #fff' }}>
                          <th>Ingredient</th>
                          <th>Formulated Weight</th>
                          <th>Actual Pour</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tintIngredients.map((ing, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #ccc' }}>
                            <td>{ing.label}</td>
                            <td style={{ color: '#00ff00' }}>{ing.totalWeight}g</td>
                            <td>
                              <input
                                type="text"
                                value={ing.actualPour}
                                onChange={(e) => {
                                  const newIng = [...tintIngredients];
                                  newIng[index].actualPour = e.target.value;
                                  setTintIngredients(newIng);
                                }}
                                style={{ fontSize: '1.5em', padding: '10px', width: '100%', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666' }}
                                ref={(el) => (tintTableRefs.current[index] = el)}
                                onKeyDown={(e) => handleTintKeyDown(e, index)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button onClick={() => {
                      const newIng = [...tintIngredients];
                      let cumulativeFactor = 1;
                      const overNotes = [];
                      newIng.forEach((ing, index) => {
                        const total = parseFloat(ing.totalWeight) || 0;
                        const poured = parseFloat(ing.actualPour) || 0;
                        if (total > 0) {
                          const factor = poured / total;
                          if (factor > 1) {
                            overNotes.push({ label: ing.label, over: (poured - total).toFixed(1) });
                          }
                          cumulativeFactor *= factor;
                        }
                      });
                      // Apply cumulative to all totals
                      newIng.forEach(ing => {
                        ing.newTotal = (parseFloat(ing.totalWeight) * cumulativeFactor).toFixed(1);
                        const poured = parseFloat(ing.actualPour) || 0;
                        ing.adjustment = Math.max(0, parseFloat(ing.newTotal) - poured).toFixed(1);
                      });
                      const newCorrections = newIng.filter(ing => parseFloat(ing.adjustment) > 0).map(ing => ({ label: ing.label, add: ing.adjustment }));
                      // Add over notes
                      overNotes.forEach(note => newCorrections.push({ label: note.label, add: `Over by ${note.over}g`, type: 'over' }));
                      console.log('Corrections:', newCorrections);
                      setCorrections(newCorrections);
                      setTintPhase('correction');
                    }} style={{ fontSize: '2em', padding: '15px', marginTop: '20px', width: '100%', backgroundColor: '#007bff', color: '#fff' }}>Finished</button>
                  </>
                ) : (
                  <>
                    <h3 style={{ fontSize: '2.5em', fontWeight: 'bold', textAlign: 'center' }}>Adjustments Needed</h3>
                    {corrections.length === 0 ? (
                      <p style={{ fontSize: '2em', textAlign: 'center' }}>No adjustments needed!</p>
                    ) : (
                      <table style={{ width: '100%', fontSize: '4.2em', border: '1px solid #fff', borderTop: '4px solid #fff', borderBottom: '4px solid #fff', textAlign: 'center', fontWeight: 'bold', borderCollapse: 'collapse', marginTop: '20px' }}>
                        <thead>
                          <tr>
                            <th style={{ borderTop: '2px solid #fff', borderBottom: '2px solid #fff' }}>Ingredient</th>
                            <th style={{ borderTop: '2px solid #fff', borderBottom: '2px solid #fff' }}>Adjustment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {corrections.map((c, idx) => (
                            <tr key={idx}>
                              <td style={{ borderTop: '2px solid #fff', borderBottom: '2px solid #fff', padding: '18px 0' }}>{c.label}</td>
                              <td style={{ borderTop: '2px solid #fff', borderBottom: '2px solid #fff', color: c.type === 'over' ? '#ff0000' : '#00ff00', padding: '18px 0' }}>{c.type === 'add' ? `Add ${c.add}g` : c.add}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
                <button onClick={() => setIsTintPopupOpen(false)} style={{ fontSize: '2em', padding: '15px', marginTop: '20px', width: '100%', backgroundColor: '#dc3545', color: '#fff' }}>Close</button>
              </div>
            </div>
          )}
          {isWebcamPopupOpen && (
            <div className="webcam-popup">
              <div className="webcam-popup-content">
                <h2>Capture Image</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
                  <div style={{ width: '100%', maxWidth: '400px' }}>
                    <video ref={videoRef} autoPlay style={{ width: '100%' }} />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexDirection: 'column', width: '100%', maxWidth: '400px' }}>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                      <button onClick={captureImage} style={{ 
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: '1px solid #fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}>Capture Photo</button>
                      <button onClick={() => setIsWebcamPopupOpen(false)} style={{ 
                        padding: '8px 16px',
                        backgroundColor: '#6c757d',
                        color: '#fff',
                        border: '1px solid #fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}>Close</button>
                    </div>
                    <div style={{ 
                      borderTop: '1px solid #ccc', 
                      paddingTop: '20px',
                      textAlign: 'center'
                    }}>
                      <p style={{ marginBottom: '10px' }}>Or upload an image:</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              handleImageChange(reader.result);
                              setIsWebcamPopupOpen(false);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        style={{ 
                          width: '100%',
                          padding: '8px',
                          backgroundColor: '#495057',
                          color: '#fff',
                          border: '1px solid #fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {isImagePopupOpen && (
            <div className="image-popup">
              <div className="image-popup-content">
                <h2>Captured Image</h2>
                <img src={uploadedImage} alt="Captured" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
                <button onClick={() => setIsImagePopupOpen(false)}>Close</button>
              </div>
            </div>
          )}
          {isSavePopupOpen && (
            <>
              <div className="overlay" />
              <div className="save-popup">
                <div className="save-popup-content">
                  {!tintId ? (
                    <>
                      <h2>Save As</h2>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%' }}>
                        <input
                          type="text"
                          value={saveAsData.colorName}
                          onChange={(e) => {
                            console.log("Color Name input:", e.target.value);
                            setSaveAsData({ ...saveAsData, colorName: e.target.value });
                          }}
                          placeholder="Color Name"
                          style={{ width: '100%', padding: '10px', fontSize: '1.2em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', borderRadius: '4px' }}
                        />
                        <input
                          type="text"
                          value={saveAsData.woodSpecies}
                          onChange={(e) => {
                            console.log("Wood Species input:", e.target.value);
                            setSaveAsData({ ...saveAsData, woodSpecies: e.target.value });
                          }}
                          placeholder="Wood Species"
                          style={{ width: '100%', padding: '10px', fontSize: '1.2em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', borderRadius: '4px' }}
                        />
                        <input
                          type="text"
                          value={saveAsData.customer}
                          onChange={(e) => {
                            console.log("Customer input:", e.target.value);
                            setSaveAsData({ ...saveAsData, customer: e.target.value });
                          }}
                          placeholder="Customer"
                          style={{ width: '100%', padding: '10px', fontSize: '1.2em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', borderRadius: '4px' }}
                        />
                        <input
                          type="date"
                          value={saveAsData.date}
                          onChange={(e) => {
                            console.log("Date input:", e.target.value);
                            setSaveAsData({ ...saveAsData, date: e.target.value });
                          }}
                          style={{ width: '100%', padding: '10px', fontSize: '1.2em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', borderRadius: '4px' }}
                        />
                        <input
                          type="text"
                          value={saveAsData.formulator}
                          onChange={(e) => {
                            console.log("Formulator input:", e.target.value);
                            setSaveAsData({ ...saveAsData, formulator: e.target.value });
                          }}
                          placeholder="Formulator"
                          style={{ width: '100%', padding: '10px', fontSize: '1.2em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', borderRadius: '4px' }}
                        />
                        <select value={saveAsData.stainType} onChange={(e) => {
                          setSaveAsData({ ...saveAsData, stainType: e.target.value });
                          console.log('Stain Type selected in popup:', e.target.value);
                        }} style={{ width: '100%', padding: '10px', fontSize: '1.2em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', borderRadius: '4px' }}>
                          <option value="">Select Stain Type</option>
                          {stainTypeOptions.map((type, index) => (
                            <option key={index} value={type}>{type}</option>
                          ))}
                        </select>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                          <button 
                            onClick={handleSaveAsSubmit}
                            style={{ 
                              padding: '12px 24px', 
                              fontSize: '1.2em', 
                              backgroundColor: '#007bff', 
                              color: '#fff', 
                              border: '1px solid #fff', 
                              borderRadius: '6px', 
                              cursor: 'pointer', 
                              transition: 'all 0.2s',
                              minWidth: '120px'
                            }}
                          >
                            Save
                          </button>
                          <button 
                            onClick={() => {
                              setIsSavePopupOpen(false);
                              if (saveResolver.current) {
                                saveResolver.current({ saved: false });
                                saveResolver.current = null;
                              }
                            }}
                            style={{ 
                              padding: '12px 24px', 
                              fontSize: '1.2em', 
                              backgroundColor: '#6c757d', 
                              color: '#fff', 
                              border: '1px solid #fff', 
                              borderRadius: '6px', 
                              cursor: 'pointer', 
                              transition: 'all 0.2s',
                              minWidth: '120px'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2>Formula Saved</h2>
                      <button 
                        onClick={() => setIsSavePopupOpen(false)}
                        style={{ 
                          padding: '12px 24px', 
                          fontSize: '1.2em', 
                          backgroundColor: '#007bff', 
                          color: '#fff', 
                          border: '1px solid #fff', 
                          borderRadius: '6px', 
                          cursor: 'pointer', 
                          transition: 'all 0.2s',
                          minWidth: '120px',
                          marginTop: '20px'
                        }}
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          {isConfirmPopupOpen && (
            <div className="confirm-popup">
              <div className="confirm-popup-content">
                <p>Would you like to save changes?</p>
                <button onClick={handleConfirmSave}>OK</button>
                <button onClick={handleConfirmNo}>No</button>
                <button onClick={handleConfirmCancel}>Cancel</button>
              </div>
            </div>
          )}
          {isFeedbackOpen && (
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#373636', padding: '20px', borderRadius: '8px', zIndex: 1000, color: '#fff', width: '400px' }}>
              <h2>Developer Feedback</h2>
              <input
                type="text"
                value={feedbackName}
                onChange={(e) => setFeedbackName(e.target.value)}
                placeholder="Your Name"
                style={{ width: '100%', padding: '10px', fontSize: '1em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666', marginBottom: '10px' }}
              />
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Enter your notes here..."
                style={{ width: '100%', height: '150px', padding: '10px', fontSize: '1em', backgroundColor: '#4a4a4a', color: '#fff', border: '1px solid #666' }}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button onClick={handleSendFeedback}>Send</button>
                <button onClick={() => setIsFeedbackOpen(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Formulator;